// VersionOverlay: the blocking "reload now" overlay for deploy safety (D-01, D-09, D-11).
//
// Non-dismissible: no close button, no Escape, no scrim click. Everything else
// on the page, including portaled modals opened before or after this mounts, is
// inert. It waits (capped) for saves the user already made, stashes registered
// edits once, counts down 10 seconds on the wall clock, and reloads. It never
// calls a save itself (D-05, D-07).
//
// Lockout mode (the live version is itself below the minimum) never counts down
// or reloads on its own: a reload would load the same refused code. It tells the
// user an update is being fixed; the watcher moves the tab on once it is.

import React from 'react';
import ReactDOM from 'react-dom';
import useDialogShell from './primitives/useDialogShell';
import Button from './primitives/Button';
import { hasBlockingSaves, hasUnkeptWork, waitForSavesToSettle, stashForReload, reloadForDeploy } from '../../utils/deployReload';
import { hasDirtySurfaces, clearStash } from '../../utils/unsavedWork';
import { AUTO_RELOAD_SECONDS, reloadLoopTripped } from '../../utils/versionWatch';

const HEADINGS = {
    waiting: 'Finishing your save…',
    timeout: 'Updating KeyFlow',
    forced: 'KeyFlow needs to reload',
    stalled: 'Still on an older version',
    lockout: 'An issue is being fixed',
};

// Phases with no countdown and no automatic reload: only the button reloads.
const WAIT_PHASES = new Set(['stalled', 'lockout']);

// A reload that has not navigated away after this long was cancelled (the
// beforeunload "Stay" choice) or blocked, so the overlay re-arms.
export const REARM_AFTER_MS = 3000;

// The rest of the page is inert while the overlay is up, so the note cannot ask
// the user to copy anything; it states the outcome instead (review WR-06).
const NOTE_FAILED = "We couldn't keep your unsaved edits. They will be lost when KeyFlow reloads.";
const NOTE_RESTORED = 'Your unsaved edits will be restored after the reload.';
const NOTE_OTHER = "Edits on other forms can't be kept.";
const NOTE_LOST = "Changes that failed to save can't be kept.";
const NOTE_LOST_OTHER = "Changes that failed to save and edits on other forms can't be kept.";

const FIELD_SELECTOR = 'input, textarea, select, [contenteditable]';

function focusInUnregisteredField() {
    const el = typeof document !== 'undefined' ? document.activeElement : null;
    if (!el || el === document.body || typeof el.matches !== 'function') return false;
    return el.matches(FIELD_SELECTOR) && !el.closest('[data-unsaved-surface]');
}

// One panel at most (UI-SPEC note rules): what is restored, then what is not.
function pickNote({ stashOk, dirty, other, lost }) {
    if (!stashOk) return NOTE_FAILED;
    const unkept = lost && other ? NOTE_LOST_OTHER : lost ? NOTE_LOST : other ? NOTE_OTHER : null;
    if (dirty && unkept) return `Your unsaved edits will be restored after the reload. ${unkept}`;
    if (dirty) return NOTE_RESTORED;
    return unkept;
}

function Icon({ spin, info }) {
    return (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
            className={spin ? 'motion-safe:animate-spin' : undefined}
            style={{ color: 'var(--color-accent)' }}
        >
            {info ? (
                <>
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 16v-4" />
                    <path d="M12 8h.01" />
                </>
            ) : (
                <path d="M21 12a9 9 0 1 1-6.2-8.55" />
            )}
        </svg>
    );
}

export default function VersionOverlay({ mode = 'timeout', immediate = false, reload, storage, now = Date.now }) {
    const getStorage = () => storage || (typeof window !== 'undefined' ? window.sessionStorage : undefined);
    const [host] = React.useState(() => document.createElement('div'));
    const [phase, setPhase] = React.useState(() => {
        // Lockout: the live version is itself refused, so reloading cannot help.
        if (mode === 'lockout') return 'lockout';
        let tripped = false;
        try { tripped = !immediate && reloadLoopTripped(getStorage(), now()); } catch { /* storage unavailable */ }
        if (tripped) return 'stalled';
        return hasBlockingSaves() ? 'waiting' : 'counting';
    });
    const [remaining, setRemaining] = React.useState(AUTO_RELOAD_SECONDS);
    const [note, setNote] = React.useState(null);
    const [announce, setAnnounce] = React.useState('');
    const [visible, setVisible] = React.useState(false);
    const reloadButtonRef = React.useRef(null);
    const firedRef = React.useRef(false);
    const otherFieldRef = React.useRef(false);
    // Read live by the orchestration and the reload: the status can move from
    // timeout to forced while this instance stays mounted (IN-05).
    const modeRef = React.useRef(mode);
    modeRef.current = mode;
    const immediateRef = React.useRef(immediate);
    const stashedRef = React.useRef(false);
    const rearmTimerRef = React.useRef(null);
    const manualBusyRef = React.useRef(false);
    const [attempt, setAttempt] = React.useState(0);

    // Inert everything else, including portals added later. The focus signal is
    // read first because inert moves focus away.
    React.useLayoutEffect(() => {
        otherFieldRef.current = focusInUnregisteredField();
        document.body.appendChild(host);
        const saved = new Map();
        const lock = (node) => {
            if (node === host || saved.has(node) || node.nodeType !== 1) return;
            saved.set(node, { inert: node.getAttribute('inert'), hidden: node.getAttribute('aria-hidden') });
            node.setAttribute('inert', '');
            node.setAttribute('aria-hidden', 'true');
        };
        Array.from(document.body.children).forEach(lock);
        const observer = new MutationObserver((records) => {
            records.forEach((r) => r.addedNodes.forEach(lock));
        });
        observer.observe(document.body, { childList: true });
        return () => {
            observer.disconnect();
            saved.forEach((prev, node) => {
                if (prev.inert === null) node.removeAttribute('inert'); else node.setAttribute('inert', prev.inert);
                if (prev.hidden === null) node.removeAttribute('aria-hidden'); else node.setAttribute('aria-hidden', prev.hidden);
            });
            if (host.parentNode) host.parentNode.removeChild(host);
        };
    }, [host]);

    React.useEffect(() => { setVisible(true); }, []);

    const doReload = React.useCallback((reason, automatic) => {
        if (firedRef.current) return;
        firedRef.current = true;
        setAnnounce('Reloading now.');
        reloadForDeploy({ reason, automatic, reload, storage: getStorage(), now: now() });
        // WR-02: still here a few seconds later means the reload did not navigate
        // (the user chose Stay at a beforeunload prompt, or it was blocked). Re-arm
        // instead of leaving a dead overlay: the button works again, and outside
        // the wait phases the save wait, stash, and countdown start over. A
        // re-armed Reload now counts down rather than firing again at once.
        clearTimeout(rearmTimerRef.current);
        rearmTimerRef.current = setTimeout(() => {
            rearmTimerRef.current = null;
            firedRef.current = false;
            immediateRef.current = false;
            setPhase((prev) => (WAIT_PHASES.has(prev) ? prev : hasBlockingSaves() ? 'waiting' : 'counting'));
            setRemaining(AUTO_RELOAD_SECONDS);
            setAttempt((n) => n + 1);
        }, REARM_AFTER_MS);
    }, [reload, storage, now]); // eslint-disable-line react-hooks/exhaustive-deps

    // CR-03: a stash written for a reload that never happened is cleared when the
    // overlay goes away without reloading (the tab became current, or the shell
    // unmounted), so no later, unrelated reload restores it. A real reload never
    // runs this cleanup, and a reload in flight keeps its stash.
    React.useEffect(() => () => {
        clearTimeout(rearmTimerRef.current);
        if (!firedRef.current && stashedRef.current) clearStash(getStorage());
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const stashNow = () => {
        const stash = stashForReload({ now: now(), storage: getStorage() });
        if (stash.ok && stash.count > 0) stashedRef.current = true;
        return stash;
    };

    // Orchestration: wait for saves, stash once, then count down on the wall clock.
    // Runs again on each re-arm (attempt).
    React.useEffect(() => {
        if (WAIT_PHASES.has(phase)) { setAnnounce(HEADINGS[phase]); return undefined; }
        let cancelled = false;
        let timer = null;
        (async () => {
            if (hasBlockingSaves()) {
                setPhase('waiting');
                setAnnounce(HEADINGS.waiting);
                await waitForSavesToSettle();
                if (cancelled) return;
            }
            // Read dirtiness after the stash has flushed debounced input, and judge
            // the note by what was stored: dirty surfaces with nothing stored (no
            // signed-in identity, or every collect threw) were not kept.
            const stash = stashNow();
            const dirty = hasDirtySurfaces();
            const kept = stash.ok && !(dirty && stash.count === 0);
            setNote(pickNote({ stashOk: kept, dirty: stash.count > 0, other: otherFieldRef.current, lost: hasUnkeptWork() }));
            if (immediateRef.current) { doReload('manual', false); return; }
            setPhase('counting');
            setAnnounce(`${HEADINGS[modeRef.current] || HEADINGS.timeout}. Reloading in ${AUTO_RELOAD_SECONDS} seconds.`);
            const startedAt = now();
            timer = setInterval(() => {
                const elapsed = now() - startedAt;
                const left = Math.max(0, Math.ceil(AUTO_RELOAD_SECONDS - elapsed / 1000));
                setRemaining(left);
                if (elapsed >= AUTO_RELOAD_SECONDS * 1000) {
                    clearInterval(timer);
                    doReload(modeRef.current, true);
                }
            }, 1000);
        })();
        return () => { cancelled = true; if (timer) clearInterval(timer); };
    }, [attempt]); // eslint-disable-line react-hooks/exhaustive-deps

    const { containerRef } = useDialogShell({
        onClose: () => {},
        closeOnEscape: false,
        trapFocus: true,
        lockScroll: true,
        initialFocusRef: reloadButtonRef,
    });

    // inert stops focus and pointer input, but not document keydown listeners: a
    // modal already open underneath would still close on Escape and run its
    // onClose (flushes, navigation, discard prompts). Swallow, in the capture
    // phase on window, Escape everywhere and every key aimed outside the dialog;
    // a Tab from outside is sent back to the dialog's button.
    React.useEffect(() => {
        const guard = (event) => {
            const container = containerRef.current;
            const inside = Boolean(container && event.target instanceof Node && container.contains(event.target));
            if (inside && event.key !== 'Escape') return;
            event.stopPropagation();
            if (event.key === 'Escape') { event.preventDefault(); return; }
            if (event.key === 'Tab') {
                event.preventDefault();
                reloadButtonRef.current?.focus();
            }
        };
        window.addEventListener('keydown', guard, true);
        return () => window.removeEventListener('keydown', guard, true);
    }, [containerRef]);

    const heading = phase === 'waiting' || WAIT_PHASES.has(phase) ? HEADINGS[phase] : (HEADINGS[mode] || HEADINGS.timeout);
    const lead = mode === 'forced' ? 'An important update is available.' : 'A new version is ready.';
    const waiting = phase === 'waiting';

    // WR-03: the stalled and lockout phases skip the orchestration, so their
    // button waits for saves already made and stashes before it reloads. Without
    // that, the edits a previous reload had just restored would be lost.
    const reloadByHand = async () => {
        if (firedRef.current || manualBusyRef.current) return;
        manualBusyRef.current = true;
        try {
            if (hasBlockingSaves()) await waitForSavesToSettle();
            stashNow();
        } finally {
            manualBusyRef.current = false;
        }
        doReload('manual', false);
    };

    const onReloadNow = () => {
        if (waiting) return;
        if (WAIT_PHASES.has(phase)) { reloadByHand(); return; }
        doReload('manual', false);
    };

    return ReactDOM.createPortal(
        <div
            className={
                'fixed inset-0 z-[9995] flex items-center justify-center p-4 bg-black/45 backdrop-blur-[3px] ' +
                'motion-safe:transition-opacity motion-safe:duration-150 ' + (visible ? 'opacity-100' : 'opacity-0')
            }
        >
            <div
                ref={containerRef}
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="version-overlay-title"
                aria-describedby="version-overlay-body"
                tabIndex={-1}
                className="w-full max-w-[420px] rounded-2xl bg-[color:var(--surface-primary)] border border-[color:var(--border-color)] shadow-[0_24px_60px_-20px_rgba(0,0,0,0.5)] p-6 focus:outline-none"
            >
                <Icon spin={waiting || phase === 'counting'} info={WAIT_PHASES.has(phase)} />
                <h2 id="version-overlay-title" className="mt-3 text-base font-semibold text-[color:var(--text-primary)]">
                    {heading}
                </h2>
                <p id="version-overlay-body" className="mt-2 text-sm text-[color:var(--text-muted)]">
                    {waiting && "KeyFlow will reload as soon as it's saved."}
                    {phase === 'counting' && (
                        <>
                            {lead} KeyFlow will reload in{' '}
                            <span aria-hidden="true" className="tabular-nums">{remaining}</span> seconds.
                        </>
                    )}
                    {phase === 'stalled' && 'Try again in a few minutes. If this keeps happening, contact support.'}
                    {phase === 'lockout' && 'KeyFlow will be ready again shortly. Try again in a few minutes.'}
                </p>
                {note && (
                    <p className="mt-4 rounded-lg border border-[color:var(--border-color)] bg-[color:var(--surface-subtle)] p-3 text-sm text-[color:var(--text-primary)]">
                        {note}
                    </p>
                )}
                <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
                    <Button
                        ref={reloadButtonRef}
                        variant="accent"
                        size="md"
                        aria-disabled={waiting ? 'true' : undefined}
                        className={'w-full sm:w-auto' + (waiting ? ' opacity-50 cursor-not-allowed' : '')}
                        onClick={onReloadNow}
                    >
                        {WAIT_PHASES.has(phase) ? 'Try reloading again' : 'Reload now'}
                    </Button>
                </div>
                <div role="status" aria-live="polite" className="sr-only">{announce}</div>
            </div>
        </div>,
        host
    );
}
